import type {
  Category, Color, FaqItem, Gift, GiftBody, GiftListItem, Media, PaginatedDocs, Post, Product, ProductSpecs,
  Redirect, SiteSettings, SlugSpace, User, UserRole, Video,
} from '@nhanho/shared'

/**
 * Wrapper `fetch` DUY NHẤT của admin — `CLAUDE.md` §4: cấm `fetch('/api/...')` rải rác.
 *
 * Base là `/api` **tương đối**: dev đi qua Vite proxy, production đi qua nginx — cùng
 * origin ở cả hai nơi, nên cookie phiên (httpOnly, SameSite=Lax) tự đi kèm.
 */
const BASE = '/api'

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly details?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
  /**
   * Lỗi theo field từ server: `details: [{field, message}]` (422 của Zod **và** 409 trùng
   * unique — API trả cùng shape). Path lồng được chuẩn hoá bằng `normalizeFieldPath()`.
   */
  get fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {}
    if (Array.isArray(this.details)) {
      for (const d of this.details as { field?: string; message?: string }[]) {
        if (!d.field || !d.message) continue
        const key = normalizeFieldPath(d.field)
        if (!out[key]) out[key] = d.message
      }
    }
    return out
  }
}

/**
 * `tags.5` → `tags`, `images.31` → `images` (mảng scalar: lỗi hiện ở ô chung);
 * `variants.2.sku`, `meta.title`, `discount.percent`, `preOrder.note`, `alt.0` giữ nguyên
 * (form có ô riêng cho từng path đó).
 */
export function normalizeFieldPath(path: string): string {
  const m = /^([a-zA-Z]+)\.\d+$/.exec(path)
  return m ? m[1]! : path
}

export interface ExplainedError {
  /** Câu để hiện toast / dưới form. Luôn có, luôn tiếng Việt, không bao giờ là HTML thô. */
  message: string
  fieldErrors: Record<string, string>
  status: number | null
}

/**
 * Chuẩn hoá MỌI loại lỗi bắt được trong form về một shape — để không còn `catch` nào
 * nuốt lỗi im lặng (mạng rớt, 502, JSON hỏng… trước đây rơi ngoài `instanceof ApiError`).
 */
export function explainError(e: unknown, fallback = 'Có lỗi xảy ra, thử lại sau'): ExplainedError {
  if (e instanceof ApiError) {
    const fieldErrors = e.fieldErrors
    let message = e.message
    if (e.status === 401) message = 'Phiên đăng nhập đã hết — đăng nhập lại rồi thao tác tiếp'
    else if (e.status === 429) message = 'Bạn thao tác quá nhanh, chờ một lát rồi thử lại'
    else if (e.status >= 500) message = `Máy chủ lỗi (${e.status}). ${e.message}`.trim()
    else if (Object.keys(fieldErrors).length) message = 'Kiểm tra lại các ô báo đỏ'
    return { message, fieldErrors, status: e.status }
  }
  if (e instanceof TypeError) {
    // `fetch` ném TypeError khi không kết nối được (API tắt, mất mạng, CORS).
    return { message: 'Không kết nối được máy chủ. Kiểm tra API đang chạy và kết nối mạng.', fieldErrors: {}, status: null }
  }
  return { message: e instanceof Error && e.message ? e.message : fallback, fieldErrors: {}, status: null }
}

/** Câu lỗi mặc định theo HTTP status khi server không trả JSON (nginx 413/502 trả HTML). */
function messageForStatus(status: number): string {
  if (status === 413) return 'Dữ liệu gửi lên quá lớn'
  if (status === 429) return 'Quá nhiều yêu cầu, thử lại sau ít phút'
  if (status === 502 || status === 503 || status === 504) return 'Máy chủ API không phản hồi'
  if (status === 404) return 'Không tìm thấy'
  return `Lỗi HTTP ${status}`
}

/** Phát ra khi server trả 401 — `AuthProvider` lắng nghe để đưa về `/login`. */
export const UNAUTHORIZED_EVENT = 'nhanho:unauthorized'

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers)
  if (init.body && !(init.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }

  const res = await fetch(`${BASE}${path}`, { ...init, headers, credentials: 'include' })

  if (res.status === 204) return undefined as T

  const text = await res.text()
  let json: unknown = null
  try {
    json = text ? JSON.parse(text) : null
  } catch {
    // server trả không phải JSON (nginx 502 chẳng hạn) → giữ text để báo lỗi
  }

  if (!res.ok) {
    const body = (json ?? {}) as { error?: string; details?: unknown }
    if (res.status === 401 && !path.startsWith('/auth/login')) {
      window.dispatchEvent(new Event(UNAUTHORIZED_EVENT))
    }
    // `text` có thể là trang HTML của nginx — không bao giờ đưa cái đó vào toast.
    const fromText = text && !/^\s*</.test(text) && text.length < 200 ? text : null
    throw new ApiError(res.status, body.error ?? fromText ?? messageForStatus(res.status), body.details)
  }
  return json as T
}

const qs = (params: Record<string, string | number | boolean | undefined | null>) => {
  const sp = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue
    sp.set(k, String(v))
  }
  const s = sp.toString()
  return s ? `?${s}` : ''
}

export type ListParams = {
  page?: number
  limit?: number
  sort?: string
  search?: string
  populate?: 1
  [key: string]: string | number | boolean | undefined | null
}

// ─── Auth ────────────────────────────────────────────────────────────────────
export const auth = {
  me: () => request<{ user: User }>('/auth/me'),
  login: (email: string, password: string) =>
    request<{ user: User }>('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  logout: () => request<{ ok: true }>('/auth/logout', { method: 'POST' }),
  changePassword: (currentPassword: string, newPassword: string) =>
    request<{ ok: true }>('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    }),
}

// ─── Tài khoản ───────────────────────────────────────────────────────────────

/**
 * Một dòng trong danh sách tài khoản (`GET /api/users`, admin-only).
 *
 * ⚠️ `role` phải luôn khớp `USER_ROLES` ở `apps/api/src/models/User.ts`. Union ở file này
 * nói dối về dữ liệu API là **trắng cả SPA** — đã cắn ngày 11/09 với `AuditRow['type']`.
 * Mọi bảng tra theo `role` phải là `Partial<Record<…>>` kèm giá trị dự phòng.
 */
export interface AdminUserRow {
  id: string
  name: string
  email: string
  role: UserRole
  slug: string | null
}

/** Body của `PATCH /api/users/me`. Cố ý KHÔNG có `role`/`email` — server cũng strip. */
export interface ProfileBody {
  name?: string
  slug?: string | null
  avatar?: string | null
  jobTitle?: string
  bio?: string
  socials?: { facebook?: string; tiktok?: string; shopee?: string }
}

export const users = {
  list: (params: ListParams = {}) => request<PaginatedDocs<AdminUserRow>>(`/users${qs(params)}`),
  updateMe: (body: ProfileBody) =>
    request<{ user: User }>('/users/me', { method: 'PATCH', body: JSON.stringify(body) }),
}

// ─── CRUD generic ────────────────────────────────────────────────────────────
function crud<T, Body = Partial<T>>(resource: string) {
  return {
    list: (params: ListParams = {}) => request<PaginatedDocs<T>>(`/${resource}${qs(params)}`),
    get: (id: string) => request<T>(`/${resource}/${id}`),
    create: (body: Body) => request<T>(`/${resource}`, { method: 'POST', body: JSON.stringify(body) }),
    update: (id: string, body: Partial<Body>) =>
      request<T>(`/${resource}/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    remove: (id: string) => request<{ ok: true; id: string }>(`/${resource}/${id}`, { method: 'DELETE' }),
  }
}

export type BulkAction = 'publish' | 'unpublish' | 'feature' | 'unfeature' | 'hot' | 'unhot' | 'new' | 'unnew' | 'delete'

/** Khối SEO gửi lên — giống nhau cho sản phẩm, bài viết và danh mục. */
export interface SeoMetaBody {
  title?: string
  description?: string
  image?: string | null
  focusKeyword?: string
  secondaryKeywords?: string[]
  canonical?: string
  noindex?: boolean
  nofollow?: boolean
  ogTitle?: string
  ogDescription?: string
}

/** Body gửi lên khi tạo/sửa sản phẩm — ref dạng id, không phải object populate. */
export interface ProductBody {
  title: string
  slug?: string
  sku?: string
  description: string
  shortDescription?: string
  category?: string | null
  tags?: string[]
  price: number
  compareAtPrice?: number | null
  stockType: Product['stockType']
  stockQuantity?: number | null
  preOrder?: { daysUntilAvailable?: number | null; estimatedDate?: string | null; note?: string | null } | null
  images?: string[]
  videos?: string[]
  colors?: string[]
  variants?: VariantBody[]
  shopeeUrl?: string
  zaloLink?: string
  published?: boolean
  featured?: boolean
  isHot?: boolean
  isNewArrival?: boolean
  order?: number
  /** `null` = không giảm. Ngày dạng ISO; để trống = vô thời hạn. */
  discount?: { percent: number; startsAt?: string | null; endsAt?: string | null } | null
  brand?: string
  faq?: FaqItem[]
  /**
   * Thông số. **Luôn gửi trọn object**, ô trống là `null` chứ không phải bỏ key: Mongoose bỏ qua
   * `undefined` trong `findByIdAndUpdate` nên thiếu key = "giữ giá trị cũ", xoá ô sẽ không ăn.
   */
  specs?: ProductSpecs
  meta?: SeoMetaBody
}

/** Một nhóm trong `GET /products/stats`. */
export interface StatGroup { count: number; items: { id: string; title: string }[] }
export interface ProductStats {
  lowStockThreshold: number
  total: StatGroup; published: StatGroup; draft: StatGroup; featured: StatGroup
  hot: StatGroup; new: StatGroup; onSale: StatGroup; saleExpiringIn7Days: StatGroup
  noImage: StatGroup; noSeo: StatGroup; outOfStock: StatGroup; lowStock: StatGroup
}

export interface VariantBody {
  id?: string
  sku: string
  /** Tên hiển thị cho khách ("Combo nơ tím"). Trống → web ghép từ màu/size, không có thì dùng SKU. */
  label?: string
  color?: string | null
  size?: string
  price: number
  compareAtPrice?: number | null
  stockQuantity?: number
  image?: string | null
  order?: number
  active?: boolean
}

export interface PostBody {
  title: string
  slug?: string
  excerpt?: string
  content: string
  featuredImage?: string | null
  /** Id của `Category` có `space: 'post'` — trước 2026-09-11 là chuỗi enum. */
  category?: string | null
  /**
   * Id của `User`. `undefined` = không gửi khoá này ⇒ server tự gán người đang đăng nhập
   * (đường của editor). `null` = gỡ tác giả — chỉ admin làm được.
   *
   * Chuỗi `author` cũ **đã bỏ** khỏi body: API không nhận ghi nữa (xem `models/Post.ts`).
   */
  authorUser?: string | null
  tags?: string[]
  publishedAt?: string | null
  published?: boolean
  faq?: FaqItem[]
  meta?: SeoMetaBody
}

export interface CategoryBody {
  /** `product` → `/san-pham/<slug>` · `post` → `/blog/<slug>`. Mặc định ở API là `product`. */
  space?: SlugSpace
  name: string
  slug?: string
  description?: string
  image?: string | null
  parent?: string | null
  order?: number
  published?: boolean
  /** Chỉ có nghĩa với `space: 'product'` — dải danh mục trang chủ. */
  showOnHomepage?: boolean
  longContentTitle?: string
  longContent?: string
  faq?: FaqItem[]
  meta?: SeoMetaBody
}

export interface CategoryNode {
  id: string
  name: string
  slug: string
  order: number
  published: boolean
  showOnHomepage: boolean
  parent: string | null
  image: Media | null
  children: CategoryNode[]
}

export const products = {
  ...crud<Product, ProductBody>('products'),
  bulk: (ids: string[], action: BulkAction) =>
    request<{ ok: true; affected: number }>('/products/bulk', {
      method: 'POST',
      body: JSON.stringify({ ids, action }),
    }),
  stats: () => request<ProductStats>('/products/stats'),
  /** Bản sao là nháp; slug/SKU tự thêm hậu tố ở server. */
  duplicate: (id: string) => request<Product>(`/products/${id}/duplicate`, { method: 'POST' }),
}

export const posts = {
  ...crud<Post, PostBody>('posts'),
  bulk: (ids: string[], action: Exclude<BulkAction, 'feature' | 'unfeature'>) =>
    request<{ ok: true; affected: number }>('/posts/bulk', {
      method: 'POST',
      body: JSON.stringify({ ids, action }),
    }),
}

export const categories = {
  ...crud<Category, CategoryBody>('categories'),
  tree: (space?: SlugSpace) =>
    request<{ tree: CategoryNode[] }>(`/categories?tree=1${space ? `&space=${space}` : ''}`),
  reorder: (items: { id: string; order: number; parent?: string | null }[]) =>
    request<{ ok: true; changed: number }>('/categories/reorder', {
      method: 'PATCH',
      body: JSON.stringify({ items }),
    }),
}

/** Trùng lặp mà chỉ server thấy được: tiêu đề SEO và từ khoá chính bị dùng ở trang khác. */
export interface SeoConflictRef { type: 'product' | 'post' | 'category'; id: string; title: string }
export interface SeoConflicts { duplicateTitle: SeoConflictRef | null; duplicateKeyword: SeoConflictRef | null }

export interface AuditRow { type: 'product' | 'post' | 'category'; id: string; title: string; slug: string }
export interface AuditGroup { count: number; items: AuditRow[] }
export interface AuditDupGroup { count: number; groups: { value: string; items: AuditRow[] }[] }
export interface SeoAudit {
  publishedTotal: number
  siteIndexingBlocked: boolean
  missingTitle: AuditGroup
  missingDescription: AuditGroup
  missingKeyword: AuditGroup
  noindexed: AuditGroup
  missingImage: AuditGroup
  thinContent: AuditGroup
  duplicateTitles: AuditDupGroup
  duplicateKeywords: AuditDupGroup
}

export const seo = {
  conflicts: (p: { kind: 'product' | 'post' | 'category'; id?: string; title?: string; keyword?: string }) =>
    request<SeoConflicts>(`/seo/conflicts${qs({ kind: p.kind, id: p.id, title: p.title, keyword: p.keyword })}`),
  audit: () => request<SeoAudit>('/seo/audit'),
}

export type SettingsBody = Partial<
  Omit<SiteSettings, 'id' | 'createdAt' | 'updatedAt' | 'defaultOgImage' | 'homeInspiration'>
> & {
  defaultOgImage?: string | null
  /**
   * Dải ảnh trang chủ — gửi lên **chỉ có `media` + `crop`**.
   *
   * `Omit` ở trên là cố ý: `SiteSettings.homeInspiration` còn có `url`/`filename`/`width`/
   * `height`, nhưng đó là thứ **server cắt ra rồi trả về**, không phải thứ client gửi đi. Để
   * nguyên kiểu của response làm kiểu của request là mời người viết sau này gửi `url` lên —
   * Zod sẽ vứt im lặng, và họ đi tìm xem "sao ảnh không đổi".
   */
  homeInspiration?: {
    media: string
    crop: { rotate: 0 | 90 | 180 | 270; x: number; y: number; size: number }
  }[]
}

export const settings = {
  get: () => request<SiteSettings>('/settings'),
  update: (body: SettingsBody) => request<SiteSettings>('/settings', { method: 'PATCH', body: JSON.stringify(body) }),
}

export interface RedirectBody {
  from: string
  to: string
  code?: 301 | 302
  note?: string
}

export const redirects = {
  ...crud<Redirect, RedirectBody>('redirects'),
  bulk: (ids: string[]) =>
    request<{ ok: true; affected: number }>('/redirects/bulk', {
      method: 'POST',
      body: JSON.stringify({ ids, action: 'delete' }),
    }),
}

/**
 * Trang quà tặng — HTML thô, chỉ vai `admin` gọi được cả 5 endpoint.
 *
 * ⚠️ **KHÔNG** dùng `crud<Gift>` nguyên bản: `GET /api/gifts` cố ý không trả `html`
 * (`.select('-html')` ở API — mỗi doc vài MB base64). Khai `list` trả `GiftListItem` để
 * `tsc` chặn ngay việc đọc `row.html`, thay vì để lỗi lộ ra lúc chạy. Đây chính là bẫy
 * "kiểu TypeScript nói dối về dữ liệu thật" ở `CLAUDE.md` §7.
 */
export const gifts = {
  list: (params: ListParams = {}) => request<PaginatedDocs<GiftListItem>>(`/gifts${qs(params)}`),
  get: (id: string) => request<Gift>(`/gifts/${id}`),
  create: (body: GiftBody) => request<Gift>('/gifts', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: string, body: Partial<GiftBody>) =>
    request<Gift>(`/gifts/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  remove: (id: string) => request<{ ok: true; id: string }>(`/gifts/${id}`, { method: 'DELETE' }),
}

export const colors = crud<Color, { name: string; hex: string; order?: number }>('colors')
export const videos = crud<Video, { title: string; url: string; order?: number }>('videos')

export interface MediaMetaBody {
  alt?: string
  title?: string
  description?: string
}

export interface TopView {
  type: 'product' | 'post' | 'category'
  id: string
  title: string
  slug: string
  views: number
}

export const views = {
  /** Xem nhiều nhất trong `days` ngày gần đây. Chỉ đọc — beacon ghi qua `apps/web`. */
  top: (days = 30, limit = 5) => request<{ days: number; docs: TopView[] }>(`/views/top?days=${days}&limit=${limit}`),
}

export const media = {
  list: (params: ListParams = {}) => request<PaginatedDocs<Media>>(`/media${qs(params)}`),
  get: (id: string) => request<Media>(`/media/${id}`),
  update: (id: string, body: MediaMetaBody) =>
    request<Media>(`/media/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  /** Sửa meta nhiều ảnh trong một lượt — mỗi dòng mang dữ liệu riêng, không phải một hành động chung. */
  bulk: (items: (MediaMetaBody & { id: string })[]) =>
    request<{ ok: true; affected: number }>('/media/bulk', {
      method: 'POST',
      body: JSON.stringify({ items }),
    }),
  remove: (id: string) => request<{ ok: true }>(`/media/${id}`, { method: 'DELETE' }),
  /**
   * Upload **một** file, có callback tiến độ (0–1).
   *
   * Dùng `XMLHttpRequest` vì `fetch` không có `upload.onprogress`. Admin gọi tuần tự từng
   * file: một ảnh hỏng không kéo cả lô, và sharp trên VPS chỉ xử lý một ảnh một lúc.
   * Cùng quy tắc với `request()`: 401 → phát `UNAUTHORIZED_EVENT`, lỗi → `ApiError`.
   */
  uploadOne: (file: File, alt: string, filename = '', onProgress?: (ratio: number) => void) =>
    new Promise<Media>((resolve, reject) => {
      const fd = new FormData()
      fd.append('files', file)
      fd.append('alt', alt)
      // Chuỗi rỗng vẫn gửi: server coi "" là "tự đặt tên", và giữ chỉ số khớp với `files`.
      fd.append('filename', filename)
      const xhr = new XMLHttpRequest()
      xhr.open('POST', `${BASE}/media`)
      xhr.withCredentials = true
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total)
      }
      xhr.onerror = () => reject(new TypeError('network'))
      xhr.onload = () => {
        let json: unknown = null
        try { json = xhr.responseText ? JSON.parse(xhr.responseText) : null } catch { /* HTML từ nginx */ }
        if (xhr.status >= 200 && xhr.status < 300) {
          const doc = (json as { docs?: Media[] } | null)?.docs?.[0]
          if (doc) resolve(doc)
          else reject(new ApiError(500, 'Server không trả về ảnh vừa upload'))
          return
        }
        if (xhr.status === 401) window.dispatchEvent(new Event(UNAUTHORIZED_EVENT))
        const body = (json ?? {}) as { error?: string; details?: unknown }
        reject(new ApiError(xhr.status, body.error ?? messageForStatus(xhr.status), body.details))
      }
      xhr.send(fd)
    }),
}
